const TRACKING_PARAMS = new Set([
  "ref",
  "refsrc",
  "mibextid",
  "__cft__",
  "__tn__",
  "locale",
  "paipv",
]);

const FACEBOOK_HOSTS = new Set(["facebook.com", "fb.com"]);

function ensureScheme(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (/^webcal:\/\//i.test(trimmed)) {
    return trimmed.replace(/^webcal:/i, "https:");
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function canonicalHost(value) {
  try {
    let host = new URL(ensureScheme(value)).hostname.toLowerCase();
    for (const prefix of ["www.", "m.", "mbasic.", "web."]) {
      if (host.startsWith(prefix)) {
        host = host.slice(prefix.length);
        break;
      }
    }
    return host;
  } catch {
    return "";
  }
}

export function isFacebookUrl(value) {
  return FACEBOOK_HOSTS.has(canonicalHost(value));
}

export function normaliseSourceUrl(value) {
  const prepared = ensureScheme(value);
  if (!prepared) return "";

  let parsed;
  try {
    parsed = new URL(prepared);
  } catch {
    return "";
  }

  parsed.protocol = "https:";
  const host = canonicalHost(parsed.toString());
  parsed.hostname = FACEBOOK_HOSTS.has(host) ? "www.facebook.com" : host;
  parsed.port = "";
  parsed.hash = "";

  for (const key of [...parsed.searchParams.keys()]) {
    if (key.toLowerCase().startsWith("utm_") || TRACKING_PARAMS.has(key.toLowerCase())) {
      parsed.searchParams.delete(key);
    }
  }
  parsed.searchParams.sort();

  let path = parsed.pathname.replace(/\/{2,}/g, "/");
  if (path.length > 1) path = path.replace(/\/+$/, "");
  parsed.pathname = path || "/";

  return parsed.toString().replace(/\/$/, parsed.pathname === "/" ? "/" : "");
}

export function inferSourceType(value) {
  const normalised = normaliseSourceUrl(value);
  if (!normalised) return "source_watch";

  const parsed = new URL(normalised);
  const path = parsed.pathname.toLowerCase();

  if (isFacebookUrl(normalised)) {
    if (path === "/groups" || path.startsWith("/groups/")) {
      return "facebook_group_hint";
    }
    if (
      path.includes("/posts/") ||
      path.includes("/videos/") ||
      path.includes("/photos/") ||
      path.includes("/events/") ||
      path.includes("/share/") ||
      path.endsWith("/permalink.php") ||
      path.endsWith("/story.php") ||
      parsed.searchParams.has("story_fbid") ||
      parsed.searchParams.has("fbid")
    ) {
      return "facebook_manual";
    }
    return "facebook_page";
  }

  if (/\.ics(?:$|\?)/i.test(normalised)) return "ics";
  return "source_watch";
}

export function sourceDefaults(type) {
  switch (type) {
    case "facebook_page":
      return { trust: "source_watch_only", active: true, monitoring_mode: "automated" };
    case "facebook_group_hint":
      return { trust: "disabled", active: true, monitoring_mode: "manual_review" };
    case "facebook_manual":
      return { trust: "disabled", active: true, monitoring_mode: "manual_review" };
    case "ics":
      return { trust: "review_required", active: true, monitoring_mode: "automated" };
    default:
      return { trust: "source_watch_only", active: true, monitoring_mode: "automated" };
  }
}

export function suggestedSourceName(value) {
  const normalised = normaliseSourceUrl(value);
  if (!normalised) return "";
  const parsed = new URL(normalised);
  const segments = parsed.pathname.split("/").filter(Boolean);

  if (!segments.length) return parsed.hostname;
  let candidate = segments[0];
  if (candidate === "groups" && segments[1]) candidate = segments[1];
  if (/^(posts|videos|photos|events|share)$/i.test(candidate) && segments[1]) {
    candidate = segments[1];
  }
  if (/^\d+$/.test(candidate)) {
    return isFacebookUrl(normalised) ? "Facebook-källa" : parsed.hostname;
  }

  try {
    candidate = decodeURIComponent(candidate);
  } catch {
    // Keep the encoded segment if decoding fails.
  }
  return candidate.replace(/[._-]+/g, " ").replace(/\s+/g, " ").trim();
}

export function sourceIdentityKey(value) {
  const normalised = normaliseSourceUrl(value);
  if (!normalised) return "";
  const parsed = new URL(normalised);
  const host = canonicalHost(normalised);
  const segments = parsed.pathname.split("/").filter(Boolean);
  const type = inferSourceType(normalised);

  if (FACEBOOK_HOSTS.has(host)) {
    if (type === "facebook_group_hint" && segments[1]) {
      return `facebook:group:${segments[1].toLowerCase()}`;
    }
    if (type === "facebook_page" && segments[0]) {
      return `facebook:page:${segments[0].toLowerCase()}`;
    }
  }
  return normalised.toLowerCase();
}

export function findDuplicateSource(value, sources = []) {
  const normalised = normaliseSourceUrl(value);
  const identity = sourceIdentityKey(value);
  if (!normalised) return null;

  return sources.find((source) => {
    const existingUrl = source?.url || "";
    return (
      normaliseSourceUrl(existingUrl) === normalised ||
      (identity && sourceIdentityKey(existingUrl) === identity)
    );
  }) || null;
}

export function prepareQuickSource({ url, namn, typ, region, kategori }) {
  const normalisedUrl = normaliseSourceUrl(url);
  if (!normalisedUrl) {
    throw new Error("Ogiltig URL");
  }

  const resolvedType = typ && typ !== "auto" ? typ : inferSourceType(normalisedUrl);
  const defaults = sourceDefaults(resolvedType);
  const resolvedName = String(namn || "").trim() || suggestedSourceName(normalisedUrl);
  if (!resolvedName) {
    throw new Error("Källans namn kunde inte härledas");
  }

  return {
    namn: resolvedName,
    url: normalisedUrl,
    typ: resolvedType,
    trust: defaults.trust,
    region: String(region || "").trim() || null,
    kategori: String(kategori || "").trim() || null,
    active: defaults.active,
    monitoring_mode: defaults.monitoring_mode,
  };
}
